/**
 * TransferHeader Component
 *
 * Displays transfer status badge, sites, dates, and notes in read-only mode.
 * @see Sprint 14 - Transferts Inter-Sites
 */

import { RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui';
import { TRANSFER_STATUS_COLORS, getTransferStatusLabel } from '@bstock/shared';
import type { TransferDetail } from '@bstock/shared';
import { formatCentimes } from '@/lib/format';
import { useFormatDate } from '@/hooks';

interface TransferHeaderProps {
  transfer: TransferDetail;
  isDestination: boolean;
  currency: string;
}

export function TransferHeader({ transfer, isDestination, currency }: TransferHeaderProps) {
  const { formatDateTime } = useFormatDate();

  return (
    <div className="border-b border-neutral-200 pb-6 space-y-4">
      <div className="flex items-center justify-between">
        <Badge variant={TRANSFER_STATUS_COLORS[transfer.status]}>
          {getTransferStatusLabel(transfer.status, isDestination)}
        </Badge>
        {transfer.totalValueCentimes > 0 && (
          <span className="text-lg font-semibold">
            {formatCentimes(transfer.totalValueCentimes, currency)}
          </span>
        )}
      </div>

      {/* US-SR-CENTRAL.1 : info read-only pour identifier que ce Transfer
          provient d'un retour fournisseur cote PDV. Pas de lien : le SR vit
          dans la brand du PDV, pas dans celle de la centrale qui regarde
          ce drawer. */}
      {transfer.supplierReturnId && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          <RotateCcw className="w-4 h-4" />
          <span className="font-medium">Retour fournisseur</span>
          {transfer.supplierReturnNumber && (
            <span className="font-mono">n°{transfer.supplierReturnNumber}</span>
          )}
        </div>
      )}

      {/* Sites */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-neutral-50 rounded-lg p-3">
          <div className="text-xs text-neutral-500 mb-1">Fournisseur</div>
          <div className="font-medium">{transfer.sourceSite.name}</div>
          {transfer.sourceSite.city && (
            <div className="text-sm text-neutral-500">{transfer.sourceSite.city}</div>
          )}
        </div>
        <div className="bg-neutral-50 rounded-lg p-3">
          <div className="text-xs text-neutral-500 mb-1">Demandeur</div>
          <div className="font-medium">{transfer.destinationSite.name}</div>
          {transfer.destinationSite.city && (
            <div className="text-sm text-neutral-500">{transfer.destinationSite.city}</div>
          )}
        </div>
      </div>

      {/* Dates */}
      <div className="text-sm text-neutral-500 space-y-1">
        <div>Cree le {formatDateTime(transfer.createdAt)}</div>
        {transfer.shippedAt && (
          <div>
            Expedie le {formatDateTime(transfer.shippedAt)}
            {transfer.shipDate && transfer.shipDate !== transfer.shippedAt && (
              <span className="text-primary-600"> (date effective : {formatDateTime(transfer.shipDate)})</span>
            )}
          </div>
        )}
        {transfer.receivedAt && (
          <div>
            Recu le {formatDateTime(transfer.receivedAt)}
            {transfer.receiveDate && transfer.receiveDate !== transfer.receivedAt && (
              <span className="text-primary-600"> (date effective : {formatDateTime(transfer.receiveDate)})</span>
            )}
          </div>
        )}
      </div>

      {transfer.notes && (
        <div className="text-sm bg-neutral-50 p-3 rounded-lg">
          <div className="text-xs text-neutral-500 mb-1">Notes</div>
          <div className="whitespace-pre-wrap">{transfer.notes}</div>
        </div>
      )}
    </div>
  );
}
